import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock3 } from 'lucide-react';
import { Badge, Card } from '@command-center/ui-system';
import { fetchClientJson } from '../../api/client-data';
import { useOrganization } from '../../organization/OrganizationProvider';
import { ClientRecordTypeStrip } from '../module/ClientRecordShell';

export interface ClientWalletStatus {
  clientId: string;
  status: 'active' | 'frozen';
  time: { minutes: string; delinquent: boolean };
  usage: { credits: string; status: 'coming_soon' };
}

export function useClientWalletStatus() {
  const organization = useOrganization();
  return useQuery({
    queryKey: ['client-wallet', organization.selectedClientId],
    queryFn: () => fetchClientJson<ClientWalletStatus>('/api/client/wallet', organization.selectedClientId!),
    enabled: Boolean(organization.selectedClientId),
    refetchInterval: 30_000,
  });
}

export function ClientWalletDelinquencyBanner() {
  const wallet = useClientWalletStatus();
  if (!wallet.data?.time.delinquent) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm" role="alert">
      <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
      <strong>Time Credit required.</strong>
      <span className="text-muted-foreground">
        Your balance is {wallet.data.time.minutes} minutes. Normal work is paused until credit is restored; reviewed performed work may still be posted.
      </span>
    </div>
  );
}

export function ClientWalletSummaryCard() {
  const wallet = useClientWalletStatus();
  if (wallet.isLoading) return <Card className="p-4 text-sm text-muted-foreground">Loading Wallet…</Card>;
  if (!wallet.data) return <Card className="p-4 text-sm text-muted-foreground">Wallet is temporarily unavailable.</Card>;
  const minutes = Number(wallet.data.time.minutes);
  return (
    <div>
      <Card className="overflow-hidden p-0 shadow-none">
        <ClientRecordTypeStrip recordTypeKey="wallet" />
        <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 font-semibold"><Clock3 className="h-4 w-4" /> Time Credit</div>
          <Badge variant={wallet.data.time.delinquent ? 'destructive' : 'success'}>
            {wallet.data.time.delinquent ? 'Credit Required' : 'Available'}
          </Badge>
        </div>
        <p className="mt-4 text-2xl font-semibold tabular-nums">{formatMinutes(minutes)}</p>
        </div>
      </Card>
    </div>
  );
}

function formatMinutes(minutes: number): string {
  const sign = minutes < 0 ? '−' : '';
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const remainder = absolute % 60;
  return `${sign}${hours}h ${remainder}m`;
}
